import type {
  Condition,
  ConditionField,
  ConditionOperator,
  OfferApprovalRules,
  RuleGroup,
} from "@shared/schema";
import { PlusCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── Field / operator display names ───────────────────────────

const FIELD_LABELS: Record<ConditionField, string> = {
  salary_base: "Salário base",
  comping_ratio: "Comping ratio (%)",
  sop_ratio: "SOP ratio (%)",
  signing_bonus: "Signing bonus",
  performance_bonus: "Bônus de performance",
  equity_shares: "Equity (shares)",
  performance_bonus_shares: "Bônus de performance (shares)",
  job_level: "Job level",
};

const NUMERIC_FIELDS: ConditionField[] = [
  "salary_base",
  "comping_ratio",
  "sop_ratio",
  "signing_bonus",
  "performance_bonus",
  "equity_shares",
  "performance_bonus_shares",
];

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  eq: "=",
  is_present: "está presente",
  not_present: "não está presente",
};

const NUMERIC_OPERATORS: ConditionOperator[] = [
  "gt",
  "gte",
  "lt",
  "lte",
  "eq",
  "is_present",
  "not_present",
];

const TEXT_OPERATORS: ConditionOperator[] = ["eq", "is_present", "not_present"];

const NO_VALUE_OPERATORS: ConditionOperator[] = ["is_present", "not_present"];

function operatorsFor(field: ConditionField): ConditionOperator[] {
  return NUMERIC_FIELDS.includes(field) ? NUMERIC_OPERATORS : TEXT_OPERATORS;
}

// ─── Default factories ────────────────────────────────────────

function defaultCondition(): Condition {
  return { type: "condition", field: "comping_ratio", operator: "gt", value: 90 };
}

function defaultGroup(): RuleGroup {
  return { type: "group", logic: "any", nodes: [defaultCondition()] };
}

// ─── Condition row ─────────────────────────────────────────────

interface ConditionRowProps {
  condition: Condition;
  onChange: (updated: Condition) => void;
  onRemove: () => void;
}

function ConditionRow({ condition, onChange, onRemove }: ConditionRowProps) {
  const operators = operatorsFor(condition.field);
  const showValue = !NO_VALUE_OPERATORS.includes(condition.operator);

  function handleFieldChange(field: ConditionField) {
    const newOperators = operatorsFor(field);
    const operator = newOperators.includes(condition.operator)
      ? condition.operator
      : (newOperators[0] ?? "gt");
    onChange({ ...condition, field, operator, value: undefined });
  }

  function handleOperatorChange(operator: ConditionOperator) {
    onChange({
      ...condition,
      operator,
      value: NO_VALUE_OPERATORS.includes(operator) ? undefined : condition.value,
    });
  }

  function handleValueChange(raw: string) {
    const isTextField = !NUMERIC_FIELDS.includes(condition.field);
    if (isTextField) {
      onChange({ ...condition, value: raw });
    } else {
      const num = parseFloat(raw);
      onChange({ ...condition, value: Number.isNaN(num) ? undefined : num });
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Field */}
      <Select value={condition.field} onValueChange={(v) => handleFieldChange(v as ConditionField)}>
        <SelectTrigger className="w-[180px] h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(FIELD_LABELS) as ConditionField[]).map((f) => (
            <SelectItem key={f} value={f} className="text-xs">
              {FIELD_LABELS[f]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator */}
      <Select
        value={condition.operator}
        onValueChange={(v) => handleOperatorChange(v as ConditionOperator)}
      >
        <SelectTrigger className="w-[160px] h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op} value={op} className="text-xs">
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value */}
      {showValue && (
        <Input
          className="w-[100px] h-8 text-xs"
          value={condition.value !== undefined ? String(condition.value) : ""}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder="valor"
        />
      )}

      {/* Remove */}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        type="button"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

// ─── Rule group (recursive) ────────────────────────────────────

interface RuleGroupEditorProps {
  group: RuleGroup;
  path: number[];
  onChange: (updated: RuleGroup) => void;
  onRemove?: () => void;
  depth: number;
}

function RuleGroupEditor({ group, path, onChange, onRemove, depth }: RuleGroupEditorProps) {
  function handleLogicChange(logic: "any" | "all") {
    onChange({ ...group, logic });
  }

  function handleNodeChange(index: number, updated: Condition | RuleGroup) {
    const newNodes = group.nodes.map((n, i) => (i === index ? updated : n));
    onChange({ ...group, nodes: newNodes });
  }

  function handleNodeRemove(index: number) {
    onChange({ ...group, nodes: group.nodes.filter((_, i) => i !== index) });
  }

  function addCondition() {
    onChange({ ...group, nodes: [...group.nodes, defaultCondition()] });
  }

  function addGroup() {
    onChange({ ...group, nodes: [...group.nodes, defaultGroup()] });
  }

  return (
    <div
      className={`border rounded-md p-3 space-y-2 ${depth > 0 ? "border-dashed bg-muted/30" : ""}`}
    >
      {/* Logic selector + remove (non-root) */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Se</span>
        <Select value={group.logic} onValueChange={(v) => handleLogicChange(v as "any" | "all")}>
          <SelectTrigger className="w-[200px] h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any" className="text-xs">
              Qualquer condição (OU)
            </SelectItem>
            <SelectItem value="all" className="text-xs">
              Todas as condições (E)
            </SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">para:</span>
        {onRemove && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive ml-auto"
            onClick={onRemove}
            type="button"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Nodes */}
      <div className="space-y-2 pl-2">
        {group.nodes.map((node, index) =>
          node.type === "condition" ? (
            <ConditionRow
              // biome-ignore lint/suspicious/noArrayIndexKey: stable index for rule editor nodes
              key={index}
              condition={node}
              onChange={(updated) => handleNodeChange(index, updated)}
              onRemove={() => handleNodeRemove(index)}
            />
          ) : (
            <RuleGroupEditor
              // biome-ignore lint/suspicious/noArrayIndexKey: stable index for rule editor nodes
              key={index}
              group={node}
              path={[...path, index]}
              depth={depth + 1}
              onChange={(updated) => handleNodeChange(index, updated)}
              onRemove={() => handleNodeRemove(index)}
            />
          ),
        )}
      </div>

      {/* Add buttons */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1 text-muted-foreground"
          onClick={addCondition}
          type="button"
        >
          <PlusCircle className="h-3.5 w-3.5" />
          Condição
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs gap-1 text-muted-foreground"
          onClick={addGroup}
          type="button"
        >
          <PlusCircle className="h-3.5 w-3.5" />
          Grupo
        </Button>
      </div>
    </div>
  );
}

// ─── Public component ──────────────────────────────────────────

export interface OfferApprovalRulesEditorProps {
  rules: OfferApprovalRules | null;
  onChange: (rules: OfferApprovalRules | null) => void;
}

export function OfferApprovalRulesEditor({ rules, onChange }: OfferApprovalRulesEditorProps) {
  if (rules === null) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Sem regras configuradas — toda oferta desta vaga exigirá aprovação manual.
        </p>
        <Button variant="outline" size="sm" onClick={() => onChange(defaultGroup())} type="button">
          Configurar regras condicionais
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        A oferta exigirá aprovação quando as condições abaixo forem verdadeiras.
      </p>
      <RuleGroupEditor
        group={rules}
        path={[]}
        depth={0}
        onChange={(updated) => onChange(updated)}
      />
      <Button
        variant="ghost"
        size="sm"
        className="text-xs text-destructive hover:text-destructive"
        onClick={() => onChange(null)}
        type="button"
      >
        Remover regras (sempre exige aprovação)
      </Button>
    </div>
  );
}
